// components/features/calls/CallDetailsModal.tsx

import { Phone, Clock, Tag, FileText, User, Calendar } from "lucide-react";
import { Call } from "../../../types/features/Call";
import BaseModal from "../../ui/Modal";

interface CallDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  call: Call | null;
}

export default function CallDetailsModal({ isOpen, onClose, call }: CallDetailsModalProps) {
  if (!call) return null;

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} min ${remainingSeconds} sec`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={call.direction === "entrant" ? "Appel entrant" : "Appel sortant"}
    >
      <div className="space-y-6">
        {/* Client */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Client</h4>
          <div className="space-y-2">
            {call.client_name ? (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <User size={14} className="text-gray-400" />
                  <span className="text-gray-800">{call.client_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={14} className="text-gray-400" />
                  <span className="text-gray-800">{call.client_phone}</span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-gray-400" />
                <span className="text-gray-500">Numéro inconnu</span>
              </div>
            )}
          </div>
        </div>

        {/* Détails */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Détails de l'appel</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock size={14} className="text-gray-400" />
              <span className="text-gray-500">Durée :</span>
              <span className="text-gray-800">{formatDuration(call.duration)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={14} className="text-gray-400" />
              <span className="text-gray-500">Date :</span>
              <span className="text-gray-800">{formatDate(call.created_at)}</span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <User size={14} className="text-gray-400 mt-0.5" />
              <span className="text-gray-500">Agent :</span>
              <span className="text-gray-800">{call.agent_name || "Agent"}</span>
            </div>
          </div>
        </div>

        {/* Tags */}
        {call.tags.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Tag size={14} />
              Tags
            </h4>
            <div className="flex flex-wrap gap-2">
              {call.tags.map((tag, idx) => (
                <span key={idx} className="text-sm bg-white px-2 py-1 rounded border border-gray-200">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {call.notes && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <FileText size={14} />
              Préoccupations du client
            </h4>
            <p className="text-gray-600 text-sm whitespace-pre-wrap">{call.notes}</p>
          </div>
        )}
      </div>
    </BaseModal>
  );
}
